import { Link } from "react-router-dom";
import { MapPin, GraduationCap, Users, Trophy, Sparkles, Heart, Globe, Award, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { THERAPIST } from "@/data/catalog";
import ReserveButton from "@/components/ReserveButton";
import useSEO from "@/hooks/useSEO";

// /biographie — parcours humain complet de Jérémy Tessier.
// Racines Sainte-Sophie · 15 ans Laurentides · MRC Pays-d'en-Haut · diplômé
// 2022 École Art-Massage · +4500 clients en 4 ans · championnat canadien 2027.
export default function Biography() {
    useSEO({
        title: "Biographie · Jérémy Tessier — Massothérapeute Laurentides depuis 2022",
        description:
            "Diplômé de l'École Art-Massage (2022), +4500 clients en 4 ans. Reconnaissance IPHM depuis 2023, étudiant en naturopathie IFN, coach de vie et praticien en hypnose, certification Abhyanga et Balinais sept. 2026. Champion canadien Nuad Thai Eastern Freestyle 2026 & Sheraton Western Freestyle 2027.",
        canonical: "https://massoclic.ca/biographie",
        ogImage: "https://massoclic.ca/icon.svg",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "Person",
            name: "Jérémy Tessier",
            jobTitle: "Massothérapeute",
            description:
                "Massothérapeute diplômé de l'École Art-Massage (2022), spécialisé en lomi-lomi, californien, yoga thaï, suédois, amma, prénatal et réflexologie. +4500 clients en 4 ans.",
            url: "https://massoclic.ca/biographie",
            telephone: THERAPIST.phone,
            email: THERAPIST.email,
            address: {
                "@type": "PostalAddress",
                streetAddress: "1436 boulevard Curé-Labelle",
                addressLocality: "Blainville",
                addressRegion: "QC",
                addressCountry: "CA",
            },
            alumniOf: { "@type": "EducationalOrganization", name: "École Art-Massage" },
            knowsAbout: [
                "Massage Lomi-Lomi", "Massage Californien", "Yoga Thaï",
                "Massage Suédois", "Amma sur chaise", "Massage Prénatal",
                "Réflexologie", "Sonothérapie", "Abhyanga (Ayurvéda)",
                "Massage Balinais", "Coaching de vie", "Hypnose thérapeutique",
            ],
        },
    });

    const techniques = [
        "Lomi-Lomi", "Californien", "Yoga Thaï", "Suédois",
        "Amma sur chaise", "Massage prénatal", "Réflexologie", "Sonothérapie",
        "Abhyanga (sept. 2026)", "Balinais (sept. 2026)",
    ];

    const credentials = [
        { icon: "iphm", label: "Reconnaissance IPHM", sub: "International Practitioners of Holistic Medicine · depuis 2023" },
        { icon: "naturopathie", label: "Étudiant en naturopathie · IFN", sub: "En formation à l'Institut de Formation en Naturopathie — pour accompagner en santé naturelle globale" },
        { icon: "coach", label: "Coach de vie certifié", sub: "Accompagnement individuel et professionnel" },
        { icon: "hypno", label: "Praticien en hypnose", sub: "Formation reconnue — approche thérapeutique" },
        { icon: "abhyanga", label: "Certification Abhyanga & Balinais", sub: "Obtention septembre 2026 — approche Occident-Orient" },
    ];

    return (
        <main data-testid="biography-page" className="overflow-hidden">
            {/* Hero */}
            <section className="relative pt-32 md:pt-40 pb-14">
                <div className="absolute inset-0 -z-10">
                    <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-[#3D8C95]/10 blur-3xl" />
                    <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#E07A5F]/10 blur-3xl" />
                </div>
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E8E2D9] mb-6">
                        <Heart size={14} className="text-[#E07A5F]" />
                        <span className="text-xs tracking-[0.2em] uppercase text-[#6E6862]">
                            Le parcours
                        </span>
                    </div>
                    <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-light text-[#2C2A29] tracking-tight leading-[1.05]">
                        Un métier{" "}
                        <span className="italic text-[#E07A5F]">de passion</span>
                        <br className="hidden sm:block" /> ancré dans les Laurentides
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#6E6862] leading-relaxed max-w-3xl">
                        Enfant de Sainte-Sophie et de sa campagne, résident des Laurentides
                        depuis 15 ans, et maintenant enraciné dans la MRC des Pays-d&apos;en-Haut
                        depuis 2 ans. Mon histoire est celle d&apos;un thérapeute qui a fait de
                        son territoire son cabinet.
                    </p>
                </div>
            </section>

            {/* Chiffres clés */}
            <section className="py-12 md:py-16 bg-white">
                <div className="max-w-5xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard icon={<Users size={22} />} value="+4500" label="Clients massés en 4 ans" color="#E07A5F" />
                    <StatCard icon={<GraduationCap size={22} />} value="2022" label="Diplômé École Art-Massage" color="#3D8C95" />
                    <StatCard icon={<MapPin size={22} />} value="15 ans" label="Résident des Laurentides" color="#C44569" />
                    <StatCard icon={<Trophy size={22} />} value="2026 · 2027" label="Championnats canadiens Nuad Thai Est & Sheraton Ouest" color="#B7A46B" />
                </div>
            </section>

            {/* Story */}
            <section className="py-14 md:py-20 bg-[#FAF7F2]">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.3em] uppercase text-[#3D8C95] mb-4">
                        Mes racines
                    </div>
                    <h2 className="font-display text-3xl md:text-5xl text-[#2C2A29] leading-tight mb-8">
                        De la campagne de Sainte-Sophie aux Pays-d&apos;en-Haut
                    </h2>
                    <div className="space-y-5 text-[#3A3735] leading-relaxed text-base md:text-lg">
                        <p>
                            J&apos;ai grandi à <b>Sainte-Sophie</b>, au cœur de la campagne des
                            Basses-Laurentides. Ce lien avec la nature — les grands champs,
                            le silence, le rythme des saisons — a façonné ma façon d&apos;être
                            au monde. Une lenteur assumée, une écoute posée, une présence.
                        </p>
                        <p>
                            J&apos;habite les Laurentides depuis <b>15 ans maintenant</b>, et
                            depuis <b>2 ans</b>, j&apos;ai posé mes racines dans la
                            <b> MRC des Pays-d&apos;en-Haut</b> — entre lacs, montagnes et
                            forêts. Ce territoire n&apos;est pas seulement mon adresse : c&apos;est
                            le décor qui m&apos;inspire chaque jour dans ma pratique.
                        </p>
                        <p>
                            Depuis <b>4 ans</b>, j&apos;exerce en cabinet et dans des spas
                            hautement réputés de la région. Cette expérience m&apos;a permis
                            de développer une pratique polyvalente, précise et attentive —
                            capable de s&apos;adapter à des clientèles très variées, de la
                            personne en surcharge mentale au sportif d&apos;endurance, en passant
                            par la femme enceinte et le retraité actif.
                        </p>
                    </div>
                </div>
            </section>

            {/* Formation & techniques */}
            <section className="py-14 md:py-20 bg-white">
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <div className="grid md:grid-cols-2 gap-10 items-start">
                        <div>
                            <div className="text-xs tracking-[0.3em] uppercase text-[#3D8C95] mb-4">
                                Formation
                            </div>
                            <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29] leading-tight mb-6">
                                Diplômé de l&apos;École Art-Massage en{" "}
                                <span className="italic text-[#E07A5F]">2022</span>
                            </h2>
                            <p className="text-[#3A3735] leading-relaxed">
                                L&apos;École Art-Massage m&apos;a formé à un ensemble large de
                                techniques manuelles, dans une approche à la fois rigoureuse
                                sur le plan anatomo-physiologique et généreuse sur le plan
                                humain. J&apos;y ai découvert que le toucher est un langage —
                                et qu&apos;on peut l&apos;apprendre toute sa vie.
                            </p>
                            <div className="mt-6 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#3D8C95]/10 border border-[#3D8C95]/30 text-xs text-[#3D8C95]">
                                <Award size={13} />
                                Reçu d&apos;assurance conforme fourni pour chaque séance
                            </div>
                        </div>
                        <div>
                            <div className="text-xs tracking-[0.3em] uppercase text-[#E07A5F] mb-4">
                                Techniques maîtrisées
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                {techniques.map((t) => (
                                    <div
                                        key={t}
                                        data-testid={`bio-technique-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="px-4 py-3 rounded-2xl bg-[#FAF7F2] border border-[#E8E2D9] text-sm font-medium text-[#2C2A29]"
                                    >
                                        <Sparkles size={12} className="inline mr-1.5 text-[#E07A5F]" />
                                        {t}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Passion cultural */}
            <section className="py-14 md:py-20 bg-[#2C2A29] text-[#FAF7F2]">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.3em] uppercase text-[#E07A5F] mb-4">
                        La curiosité comme moteur
                    </div>
                    <h2 className="font-display text-3xl md:text-5xl leading-tight mb-6">
                        Passionné par les{" "}
                        <span className="italic text-[#E07A5F]">massages culturels</span>{" "}
                        du monde entier
                    </h2>
                    <p className="text-[#FAF7F2]/80 leading-relaxed text-base md:text-lg">
                        Le lomi-lomi hawaïen et son mouvement de vagues. Le yoga thaï et ses
                        étirements assistés inspirés du bouddhisme. Le californien esalien
                        et son enveloppement continu. Le suédois européen et sa précision
                        scientifique. L&apos;amma japonais et son toucher intuitif.
                    </p>
                    <p className="mt-4 text-[#FAF7F2]/80 leading-relaxed text-base md:text-lg">
                        Chaque tradition porte une vision du corps, une philosophie du soin,
                        une intelligence du geste. Ma pratique intègre ces héritages non pas
                        comme un catalogue, mais comme un vocabulaire vivant — que j&apos;adapte
                        à chaque personne, à chaque séance.
                    </p>
                    <div className="mt-6 flex items-center gap-2 text-xs text-[#FAF7F2]/60">
                        <Globe size={14} className="text-[#E07A5F]" />
                        Hawaï · Thaïlande · Californie · Suède · Japon · Québec
                    </div>
                </div>
            </section>

            {/* Championship 2027 */}
            <section className="py-14 md:py-20 bg-[#FAF7F2]">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <div className="rounded-3xl border-2 border-[#B7A46B]/40 bg-white p-6 md:p-10">
                        <div className="flex items-start gap-4 mb-6">
                            <Trophy size={32} className="text-[#B7A46B] shrink-0 mt-1" />
                            <div>
                                <div className="text-xs tracking-[0.3em] uppercase text-[#B7A46B] mb-2">
                                    Prochain défi
                                </div>
                                <h2 className="font-display text-3xl md:text-4xl text-[#2C2A29] leading-tight">
                                    Championnats canadiens de{" "}
                                    <span className="italic text-[#B7A46B]">Freestyle</span>
                                </h2>
                                <div className="mt-2 text-sm text-[#6E6862]">
                                    Deux compétitions officielles · <b>2026 & 2027</b>
                                </div>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4 mb-6">
                            <div className="rounded-2xl bg-[#FAF7F2] p-5 border border-[#E8E2D9]">
                                <div className="text-xs uppercase tracking-widest text-[#3D8C95] mb-2 font-medium">
                                    Nuad Thai Montréal · Août 2026
                                </div>
                                <div className="font-display text-lg text-[#2C2A29] leading-tight">
                                    Catégorie <span className="italic text-[#3D8C95]">Eastern Freestyle</span>
                                </div>
                                <p className="text-sm text-[#6E6862] mt-2 leading-snug">
                                    Championnat canadien Nuad Thai — techniques orientales et libre expression du geste dans la tradition thaïlandaise.
                                </p>
                            </div>
                            <div className="rounded-2xl bg-[#FAF7F2] p-5 border border-[#E8E2D9]">
                                <div className="text-xs uppercase tracking-widest text-[#E07A5F] mb-2 font-medium">
                                    Sheraton Laval · Avril 2027
                                </div>
                                <div className="font-display text-lg text-[#2C2A29] leading-tight">
                                    Catégorie <span className="italic text-[#E07A5F]">Western Freestyle</span>
                                </div>
                                <p className="text-sm text-[#6E6862] mt-2 leading-snug">
                                    Championnat canadien de massothérapie freestyle — le thérapeute compose son propre soin devant jury.
                                </p>
                            </div>
                        </div>
                        <p className="text-[#3A3735] leading-relaxed">
                            Représenter les Laurentides sur les deux plus grandes scènes canadiennes de massothérapie freestyle
                            — Est puis Ouest — est un privilège que je prépare avec joie et rigueur. Le format libre, où le
                            thérapeute compose son propre soin devant jury, est l&apos;expression la plus pure de ce métier :
                            intuition, technique, présence, créativité.
                        </p>
                    </div>
                </div>
            </section>

            {/* Reconnaissances & certifications */}
            <section className="py-14 md:py-20 bg-white">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.3em] uppercase text-[#3D8C95] mb-4">
                        Reconnaissances & certifications
                    </div>
                    <h2 className="font-display text-3xl md:text-5xl text-[#2C2A29] leading-tight mb-10">
                        Une pratique <span className="italic text-[#E07A5F]">multidisciplinaire</span>{" "}
                        et reconnue
                    </h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        {credentials.map((c) => (
                            <div
                                key={c.label}
                                data-testid={`bio-credential-${c.icon}`}
                                className="rounded-2xl bg-[#FAF7F2] border border-[#E8E2D9] p-5"
                            >
                                <div className="flex items-start gap-3">
                                    <Award size={20} className="text-[#E07A5F] shrink-0 mt-0.5" />
                                    <div>
                                        <div className="font-display text-lg text-[#2C2A29] leading-tight">
                                            {c.label}
                                        </div>
                                        <div className="text-sm text-[#6E6862] mt-1 leading-snug">
                                            {c.sub}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="mt-8 text-[#3A3735] leading-relaxed text-base md:text-lg">
                        En parallèle du toucher, j&apos;accompagne également en <b>coaching de vie</b>
                        {" "}et <b>hypnose thérapeutique</b>. Ces trois approches — corps, mental,
                        inconscient — se répondent naturellement. La certification Abhyanga et
                        Balinais que j&apos;obtiendrai en septembre 2026 vient peaufiner ma pratique
                        pour offrir une massothérapie véritablement complète, de l&apos;Occident
                        à l&apos;Orient.
                    </p>
                </div>
            </section>

            {/* Closing */}
            <section className="py-14 md:py-24">
                <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
                    <div className="text-xs tracking-[0.3em] uppercase text-[#E07A5F] mb-4">
                        Aujourd&apos;hui
                    </div>
                    <h2 className="font-display text-3xl md:text-5xl text-[#2C2A29] leading-tight mb-6">
                        La passion de mon métier est à son{" "}
                        <span className="italic text-[#E07A5F]">apogée</span>
                    </h2>
                    <p className="text-[#3A3735] leading-relaxed text-base md:text-lg max-w-2xl mx-auto">
                        Plus de 4500 clients accompagnés, une formation continue qui ne
                        s&apos;arrête jamais, un cabinet à Blainville, un service à domicile
                        sur toutes les Laurentides — et surtout, une joie intacte au moment
                        où mes mains rencontrent chaque nouveau corps. Merci de faire partie
                        de cette histoire.
                    </p>
                    <div className="mt-10 flex flex-wrap gap-3 justify-center">
                        <ReserveButton
                            mailSubject="Prise de contact — Après la biographie"
                            mailBody={"Bonjour Jérémy,\n\nJ'ai lu votre biographie et je souhaite en savoir plus / réserver une séance.\n\nMerci !"}
                        />
                        <a href={`tel:${THERAPIST.phone}`}>
                            <Button
                                variant="outline"
                                className="rounded-full border-[#3D8C95] text-[#3D8C95] hover:bg-[#3D8C95] hover:text-white px-8 py-6"
                            >
                                <Phone size={16} className="mr-2" />
                                {THERAPIST.phone}
                            </Button>
                        </a>
                    </div>
                </div>
            </section>
        </main>
    );
}

function StatCard({ icon, value, label, color }) {
    return (
        <div
            data-testid={`bio-stat-${value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="rounded-3xl p-5 bg-white border border-[#E8E2D9]"
        >
            <div style={{ color }} className="mb-3">{icon}</div>
            <div className="font-display text-3xl text-[#2C2A29]">{value}</div>
            <div className="text-xs text-[#6E6862] mt-1 leading-snug">{label}</div>
        </div>
    );
}
